import { createTheme } from "@mui/material/styles";

const colors = {
  primary: {
    main        : "#152BFF",
    light       : "#788EFF",
    contrastText: "#ffffff",
    logoBlack   : "#0A0B0E",
    logoMain    : "#2543FF",
    logoGreen   : "#73F600",
  },
  warning: { main: "#DE6944", },
  hover  : {
    main : "rgba(21, 43, 255, 0.6)",
    light: "#F5F9FF",
  },
  grayscale: {
    black: "#000000",
    g90  : "#202224",
    g70  : "#696F79",
    g50  : "#8692A6",
    g45  : "#979797",
    g40  : "#C4C4C4",
    g30  : "#D8D8D8",
    g20  : "#D4D4D4",
    g10  : "#F5F5F5",
    white: "#FFFFFF",
  },
  tags: {
    green: {
      main: "#E6F5EE",
      dark: "#069855",
    },
    yellow: {
      main: "#FBF5E8",
      dark: "#D39C1D",
    },
    purple: {
      main: "#F3E3FF",
      dark: "#871DD3",
    },
    blue: {
      main: "#D9DFFF",
      dark: "#152BFF",
    },
    red: {
      main: "#FFDFDF",
      dark: "#D62525",
    },
  },
  statusColors: {
    "client-draft"        : "#F5F5F5",
    "intro-call-cancelled": "#F5F5F5",
    "intro-call-scheduled": "#FFF4E6",
    "waiting-for-info"    : "#FFE0B2",
    "in-progress"         : "#E3F2FD",
    "estimate-sent"       : "#E1F5FE",
    "estimate-reviewed"   : "#F3E5F5",
    "contract-sent"       : "#E8F5E9",
    "converted"           : "#1976D2",
    "rejected"            : "#FFEBEE",
    "on-hold"             : "#F5F5F5",
  },
  statusTextColors: {
    "client-draft"        : "#000000",
    "intro-call-cancelled": "#616161",
    "intro-call-scheduled": "#FF8C00",
    "waiting-for-info"    : "#FF6F00",
    "in-progress"         : "#1976D2",
    "estimate-sent"       : "#0277BD",
    "estimate-reviewed"   : "#7B1FA2",
    "contract-sent"       : "#2E7D32",
    "converted"           : "#FFFFFF",
    "rejected"            : "#C62828",
    "on-hold"             : "#616161",
  },
};

const borderRadius = { default: 0, };

// Global scaling factor (kept for spacing and component overrides; heading
// typography below uses fluid clamp() instead).
const scaleFactor = 0.85;

/**
 * Fluid font-size helper. Interpolates linearly between minPx at minVw
 * and maxPx at maxVw; clamps at both ends.
 */
const fluidSize = (minPx: number, maxPx: number, minVw = 320, maxVw = 1440) => {
  const slope = (maxPx - minPx) / (maxVw - minVw);
  const intercept = minPx - slope * minVw;

  return `clamp(${minPx}px, ${intercept.toFixed(2)}px + ${(slope * 100).toFixed(2)}vw, ${maxPx}px)`;
};

export const mainTheme = createTheme({
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    // Headings fluid between mobile (320px) and desktop (1440px).
    h1        : { fontSize: fluidSize(24, 34), },
    h2        : { fontSize: fluidSize(20, 27), },
    h3        : { fontSize: fluidSize(18, 24), },
    h4        : {
      fontSize  : fluidSize(16, 20),
      fontWeight: 700,
    },
    h5   : { fontSize: fluidSize(15, 17), },
    h6   : { fontSize: fluidSize(13, 14), },
    // Body + small text stay fixed (already small enough on mobile).
    body1: {
      fontWeight: 400,
      fontSize  : `${16 * scaleFactor}px`,
    },
    body2    : { fontSize: `${14 * scaleFactor}px`, },
    subtitle1: { fontSize: `${16 * scaleFactor}px`, },
    subtitle2: {
      fontWeight: 400,
      fontHeight: `${3 * scaleFactor}rem`,
      color     : colors.grayscale.g50,
      fontSize  : `${14 * scaleFactor}px`,
    },
    caption : { fontSize: `${12 * scaleFactor}px`, },
    overline: { fontSize: `${10 * scaleFactor}px`, },
  },
  palette   : { ...colors, },
  spacing   : (factor: number) => `${8 * factor * scaleFactor}px`,
  components: {
    MuiRadio    : { styleOverrides: { root: { ":not(.Mui-checked)": { color: colors.grayscale.g20, }, }, }, },
    MuiFormGroup: { styleOverrides: { root: { "&.MuiRadioGroup-root": { "& .MuiFormControlLabel-label": { fontSize: `${14 * scaleFactor}px`, }, }, }, }, },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.default,
          width       : "100%",
          fontSize    : `${16 * scaleFactor}px`,

          "&:hover": { borderColor: colors.primary.main, },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius                        : borderRadius.default,
          "& > input"                         : { paddingLeft: `${12 * scaleFactor}px`, },
          "& .MuiOutlinedInput-notchedOutline": {
            // borderWidth: 2,
            color: colors.primary.main,
          },
        },
      },
    },
    MuiListItemButton: {
      variants: [
        {
          props: { color: "light" },
          style: ({ theme }) => ({
            display  : "flex",
            columnGap: `${12 * scaleFactor}px`,
            padding  : 0,
            width    : 1,

            "& > div > div": { transition: "0.3s", },
            "&:hover"      : {
              "& > div > div": {
                backgroundColor: colors.hover.main,
                "& span"       : { color: theme.palette.grayscale.white, },
              },
            },
          }),
        },
      ],
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            backgroundColor: colors.grayscale.white,

            "&:hover": { backgroundColor: colors.grayscale.white, },
          },

          "&:hover": { backgroundColor: colors.grayscale.white, },
        },
      },
    },
    MuiButton: {
      variants: [
        {
          props: { color: "dark" },
          style: ({ theme }) => ({
            borderColor : "transparent",
            color       : theme.palette.grayscale.black,
            borderRadius: 1,
          }),
        },
        {
          props: { size: "default" },
          style: () => ({ height: `${52 * scaleFactor}px`, }),
        },
        {
          props: { size: "small" },
          style: () => ({ height: `${40 * scaleFactor}px`, }),
        },
      ],
      styleOverrides: {
        root: {
          borderRadius : borderRadius.default,
          borderWidth  : 2,
          fontSize     : `${14 * scaleFactor}px`,
          textTransform: "none",
        },
      },
    },

    MuiTypography: {
      variants: [
        {
          props: { variant: "illuminated" },
          style: ({ theme }) => ({
            color     : theme.palette.primary.main,
            fontSize  : `${12 * scaleFactor}px`,
            fontWeight: 500,
          }),
        },
        {
          props: { color: "textSecondary" },
          style: ({ theme }) => ({ color: theme.palette.grayscale.g70, }),
        },
      ],
      styleOverrides: {
        h4   : { fontWeight: 700, },
        body1: {
          fontWeight: 400,
          fontSize  : `${16 * scaleFactor}px`,
        },
        subtitle2: {
          fontWeight: 400,
          fontHeight: `${3 * scaleFactor}rem`,
          color     : colors.grayscale.g50,
        },
      },
    },
    MuiChip: {
      variants: [
        {
          props: { color: "tagGreen" },
          style: ({ theme }) => ({
            background : theme.palette.tags.green.main,
            borderColor: theme.palette.tags.green.dark,
            color      : theme.palette.tags.green.dark,
          }),
        },
        {
          props: { color: "tagBlue" },
          style: ({ theme }) => ({
            background : theme.palette.tags.blue.main,
            borderColor: theme.palette.tags.blue.dark,
            color      : theme.palette.tags.blue.dark,
          }),
        },
        {
          props: { color: "tagYellow" },
          style: ({ theme }) => ({
            background : theme.palette.tags.yellow.main,
            borderColor: theme.palette.tags.yellow.dark,
            color      : theme.palette.tags.yellow.dark,
          }),
        },
        {
          props: { color: "tagRed" },
          style: ({ theme }) => ({
            background : theme.palette.tags.red.main,
            borderColor: theme.palette.tags.red.dark,
            color      : theme.palette.tags.red.dark,
          }),
        },
        {
          props: { color: "tagPurple" },
          style: ({ theme }) => ({
            background : theme.palette.tags.purple.main,
            borderColor: theme.palette.tags.purple.dark,
            color      : theme.palette.tags.purple.dark,
          }),
        },
        {
          props: { color: "tagWhite" },
          style: ({ theme }) => ({
            background : theme.palette.grayscale.g10,
            borderColor: theme.palette.grayscale.g10,
            color      : theme.palette.grayscale.g70,
          }),
        },
      ],
      styleOverrides: {
        root: {
          borderRadius   : borderRadius.default,
          backgroundColor: colors.primary.light,
          fontSize       : `${12 * scaleFactor}px`,
          fontWeight     : 500,
          color          : colors.grayscale.white,
        },
        filled: { color: colors.grayscale.white, },
      },
    },
    MuiTableContainer: { styleOverrides: { root: { marginTop: `${3 * scaleFactor}rem`, }, }, },
    MuiTableHead     : {
      styleOverrides: {
        root: {
          background            : colors.grayscale.g10,
          "& .MuiTableCell-head": {
            fontWeight      : 400,
            fontSize        : `${16 * scaleFactor}px`,
            height          : `${68 * scaleFactor}px`,
            border          : 0,
            ":first-of-type": { borderRadius: "1em 0 0 1em", },
            ":last-child"   : { borderRadius: "0 1em 1em 0", },
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-body": {
            fontWeight: 400,
            fontSize  : `${16 * scaleFactor}px`,
          },
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          width       : `${60 * scaleFactor}px`,
          height      : `${60 * scaleFactor}px`,
          borderRadius: 0,
          borderColor : colors.grayscale.g30,
          fontSize    : `${16 * scaleFactor}px`,
          fontWeight  : 400,
        },
      },
    },
    MuiCard: {
      variants: [
        {
          props: { variant: "projectCard" },
          style: ({ theme }) => ({
            padding    : `${1 * scaleFactor}rem `,
            borderColor: theme.palette.grayscale.g30,
            ":hover"   : { borderColor: colors.primary.main, },
          }),
        },
        {
          props: { variant: "projectCardFilled" },
          style: ({ theme }) => ({
            padding           : `${1 * scaleFactor}rem `,
            borderColor       : theme.palette.primary.main,
            backgroundColor   : theme.palette.primary.main,
            color             : theme.palette.grayscale.white,
            "& .MuiStack-root": { borderColor: theme.palette.primary.main, },
            ":hover"          : {
              backgroundColor   : theme.palette.primary.light,
              borderColor       : theme.palette.primary.light,
              "& .MuiStack-root": { borderColor: theme.palette.primary.light, },
            },
          }),
        },
      ],
      styleOverrides: {
        root: {
          border      : `2px solid ${colors.primary.main}`,
          padding     : `${1 * scaleFactor}rem ${1.75 * scaleFactor}rem`,
          borderRadius: 0,
          height      : "100%",
        },
      },
    },
  },
});
